import { ApiError, devSession } from '@platform/dao';
import type { HttpClient } from '@platform/dao/HttpClient';
import type { IWorkspace } from '@platform/core/types';
import type { IAuthOptions } from '../types/IAuthOptions';
import type { IAuthSession } from '../types/IAuthSession';
import type { IAuthUser } from '../types/IAuthUser';
import type { ILoginRequest } from '../types/ILoginRequest';
import type { IMfaSetup } from '../types/IMfaSetup';
import type { IObserverPeople } from '../types/IObserverPeople';
import type { IObserverSession } from '../types/IObserverSession';
import type { IOnboardingWrite } from '../types/IOnboardingWrite';
import type { IRegisterRequest } from '../types/IRegisterRequest';
import type { SsoProvider } from '../types/SsoProvider';
import { toAuthOptions, toAuthSession, toAuthUser, userOf } from './authWire';
import { legacyAuthOptions } from './legacyAuthOptions';

/** Сервер без API входу (до Stage 6): маршрут не знайдено, метод не дозволено, не реалізовано. */
const MISSING = new Set([404, 405, 501]);
const missing = (e: unknown): boolean => e instanceof ApiError && MISSING.has(e.status);

/**
 * Вхід, реєстрація, сесія й режим спостерігача (контракт AUTH, 07-Security §1): сесію тримає HttpOnly-cookie сервера (BFF), SPA токенів
 * не бачить. Після входу через API заголовок X-Dev-User більше не надсилається (cookie — єдина особа); сервер без API входу (до Stage 6)
 * впускає через схему Dev — тоді e-mail стає Dev-користувачем транспорту, як у попередньому вході розробки.
 */
export class AuthDao {
  constructor(private readonly http: HttpClient) {}

  async options(): Promise<IAuthOptions> {
    try { return toAuthOptions(await this.http.get('/auth/options')); } catch (e) { if (missing(e)) return legacyAuthOptions; throw e; }
  }

  /** Поточна сесія або null (анонімно — 401; сервер без API входу — теж null). */
  async session(): Promise<IAuthSession | null> {
    try { return toAuthSession(await this.http.get('/auth/me')); } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || MISSING.has(e.status))) return null;
      throw e;
    }
  }

  async login(body: ILoginRequest): Promise<IAuthUser> {
    try { return this.signedIn(await this.http.post('/auth/login', body)); } catch (e) { if (missing(e)) return this.viaDevHeader(body.email); throw e; }
  }

  async register(body: IRegisterRequest): Promise<IAuthUser> { return this.signedIn(await this.http.post('/auth/register', body)); }

  /**
   * Підключення другого фактора під час входу (після відповіді mfa_setup_required): сервер упізнає вхід за короткочасним квитком у
   * HttpOnly-cookie і повертає секрет та otpauth-адресу; повторний виклик у межах квитка — той самий секрет.
   */
  mfaEnrol(): Promise<IMfaSetup> { return this.http.post<IMfaSetup>('/auth/mfa/enrol'); }

  /** Перший код із застосунку: сервер вмикає TOTP і відкриває сесію (відповідь — як у /auth/login). */
  async mfaEnrolConfirm(code: string): Promise<IAuthUser> { return this.signedIn(await this.http.post('/auth/mfa/enrol/confirm', { code })); }

  /** Єдиний вхід у Development (сервер повертає сесію демо-облікового запису, як sso() прототипу); у продуктиві — переадресація форми. */
  async sso(provider: SsoProvider, demoEmail?: string | null): Promise<IAuthUser> {
    try { return this.signedIn(await this.http.post(`/auth/sso/${provider}`)); } catch (e) {
      if (missing(e) && demoEmail) return this.viaDevHeader(demoEmail);
      throw e;
    }
  }

  /** Вихід: сервер очищає cookie сесії; Dev-користувач транспорту скидається завжди. */
  async logout(): Promise<void> {
    try { await this.http.post('/auth/logout'); } catch (e) { if (!missing(e) && !(e instanceof ApiError && e.status === 401)) throw e; } finally { devSession.user = null; }
  }

  people(q: string): Promise<IObserverPeople> { return this.http.get('/observer/people', { q: q || undefined }); }

  /** Сесія спостерігача: особа визначається cookie сервера, тож Dev-користувача транспорту скинуто ще до запиту. */
  async startObserver(personId: string): Promise<IObserverSession> {
    devSession.user = null;
    const body = await this.http.post<{ person?: unknown; workspaceId?: string }>('/observer/sessions', { personId });
    return { person: toAuthUser(body?.person), workspaceId: body?.workspaceId ?? '' };
  }

  async endObserver(): Promise<void> {
    try { await this.http.delete('/observer/sessions'); } catch (e) { if (!(e instanceof ApiError && (e.status === 401 || e.status === 404))) throw e; }
  }

  /** Новий робочий простір з екрана «Створити робочий простір» (POST /workspaces з адресою й зразковими записами). */
  onboard(body: IOnboardingWrite): Promise<IWorkspace> { return this.http.post('/workspaces', body); }

  private signedIn(body: unknown): IAuthUser {
    devSession.user = null;
    return userOf(body);
  }

  private viaDevHeader(email: string): IAuthUser {
    const user = email.trim().toLowerCase();
    devSession.user = user;
    return { id: '', displayName: user, email: user };
  }
}
